/**
 * Profile data — shared constants and helpers.
 *
 * Used by:
 *   - SignupPage           (initial profile creation, multi-step wizard)
 *   - ProfilePage          (later edits, single page)
 *   - DashboardPage        (reads the same labels for the profile summary card)
 *
 * Keep the codes in sync with BACKEND_CONTRACT.md. The codes are the
 * values sent to / received from the backend; the labels are translated
 * client-side via i18n keys.
 */

/**
 * Gender enum codes. Must match BACKEND_CONTRACT.md `gender` enum.
 * Display labels live under `signup.fields.gender.<code>` in i18n.
 */
export const GENDER_CODES = ['male', 'female', 'prefer_not_to_say'];

/**
 * Suggested chronic-disease codes shown in the Autocomplete dropdown.
 * Users can also type custom values (freeSolo) — the backend stores
 * those verbatim. Codes are English snake_case slugs; translations
 * live under `signup.diseases.<code>` in i18n.
 *
 * Tech debt: this list is frontend-hardcoded. When the backend grows a
 * /lookups endpoint, fetch via TanStack Query instead (see CLAUDE.md
 * tech debt #5).
 */
export const CHRONIC_DISEASE_CODES = [
  'diabetes',
  'hypertension',
  'asthma',
  'heart_disease',
  'kidney_disease',
  'thyroid',
  'arthritis',
  'migraine',
];

/**
 * Translate a known chronic-disease code to its localized label.
 * Falls back to the raw code if it's a user-entered freeSolo value
 * not in our predefined list — that way custom conditions still
 * render readably on the dashboard and review screen.
 *
 * @param {string} code
 * @param {(key: string) => string} t  i18next `t` function
 * @returns {string}
 */
export function getDiseaseLabel(code, t) {
  const key = `signup.diseases.${code}`;
  const translated = t(key);
  // i18next returns the key itself when no translation exists.
  return translated === key ? code : translated;
}
